/**
 * /enter-key/:projectId/:env?name=NAME — 「キーを 1 つ入れるだけの小さな画面」（2026-10-04 creator/chat-secrets）。
 *
 * WHY: founder「バイブコーダーは API という言葉や、シークレットキーという言葉を知らない前提です」「できればすべてチャットで完結できるといい」。
 *   AI（MCP の functions_secret_set）がチャットの中のボタン（URL モードの elicitation）でこのページを開く。名前と入れる先は AI が決めて
 *   URL に入れてある = 本人は貼って「保存する」を押すだけ。保存したら「チャットに戻ってください」。MCP は本体の一覧の更新時刻で保存を
 *   見つけて、チャットに「保存しました（値は見ていません）」を返す（apps/mcp/src/tools/secretInput.ts）。判断は ../enterKey.ts（+ test）。
 *
 * ログインしていなければ App.tsx のログイン前の木が /login へ送り、ログイン後に `state.from` でここへ戻る（ConfirmAction と同じ）。
 *
 * 値の扱い（FunctionSecretsCard と同じ決まり・enterKey.test.ts がソースを読んで確かめる）:
 *   - 入力は type="password"・パスワード管理の保存の提案を出さない（ダッシュボードのログインのパスワードと取り違えない）
 *   - フォームは method="post"（React が止まっても値が URL に乗らない）
 *   - 送ったら成功でも失敗でも欄を空にする（finally）・ログに出さない
 *   - 公開中のアプリ（production）は確認を挟む（confirmBeforePut = 今の確認の決まり。開いただけでは何も変わらない）
 * 守り: 書き込みはログインしたセッションの PUT（本体がプロジェクトの持ち主かを確かめる）。URL を誰かに送られても、
 *   その人のアカウントのプロジェクトでなければ本体が断る。
 */
import { useRef, useState, type FormEvent } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { api, ApiError, envPath, type ProjectDetail } from '../api';
import { liveUrlOf } from '../confirmPlain';
import { ConfirmDialog } from '../components/ui';
import { useResource } from '../hooks';
import { errorText, useT, type MessageKey } from '../i18n';
import { enterKeyErrorKey, readEnterKey, type EnterKeyTarget } from '../enterKey';
import { PRODUCT_NAME } from '../productName';
import { confirmBeforePut, secretPath, secretValueIssue } from '../functionSecrets';
import { AuthShell } from './AccountRecovery';

export function EnterKeyPage() {
  const { t } = useT();
  const params = useParams();
  const location = useLocation();
  const target = readEnterKey(params, location.search, PRODUCT_NAME);
  if (!target) {
    return (
      <AuthShell title={t('enterKey.title')}>
        <p className="alert alert-error" role="alert">
          {t('enterKey.badLink')}
        </p>
      </AuthShell>
    );
  }
  return <EnterKeyForm {...target} />;
}

function EnterKeyForm({ projectId, env, name, service, hint }: EnterKeyTarget) {
  const { t } = useT();
  // プロジェクトの名前（どのプロジェクトのキーか分かるように小さく出す）。読めなくても入力はできる（本体が PUT で持ち主を確かめる）
  const project = useResource<{ project: ProjectDetail }>(`/v1/projects/${projectId}`);
  // アプリの本物の URL（「公開中のアプリ（https://…）で使います」に出す）。本人のセッションで hosting を読むだけ（ConfirmAction と同じ）。
  // WHY URL を出すか: founder 2026-10-04「抽象的にすることでどうとでも意味を持つようなことばにしないで」= どのアプリかを本物で言う
  const hosting = useResource<{ url?: unknown }>(`${envPath(projectId, env)}/hosting`);
  const appUrl = liveUrlOf(hosting.data?.url);
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const issue = secretValueIssue(value);

  const put = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(secretPath(`${envPath(projectId, env)}/functions`, name), { method: 'PUT', body: { value } });
      setDone(true);
    } catch (e) {
      if (e instanceof ApiError) {
        const key = enterKeyErrorKey(e.code);
        setErr(errorText(e, key ? { [e.code]: key as MessageKey } : undefined));
      } else setErr(String(e));
    } finally {
      // 値は成功でも失敗でも消す（再表示しない・この画面に残る時間を最短に）
      setValue('');
      setBusy(false);
      setConfirm(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (busy || issue) return;
    if (confirmBeforePut(env)) setConfirm(true);
    else void put();
  };

  if (done) {
    return (
      <AuthShell title={t('enterKey.doneTitle')}>
        <p className="alert alert-ok" role="status">
          {t('enterKey.doneBody')}
        </p>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            setDone(false);
            setTouched(false);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
        >
          {t('enterKey.again')}
        </button>
      </AuthShell>
    );
  }

  return (
    // 見出し = 何のキーか（service があれば「OpenAI のキーを貼ってください」・無ければ名前）。その下に hint（どこでもらった・何で始まるか）。
    // service / hint は AI が書いた文 = React がテキストとして描く（HTML にならない）。悪用の形は readEnterKey（keyLabelOk）で落としてある
    <AuthShell title={service ? t('enterKey.titleFor', { service }) : t('enterKey.titleNamed', { name })}>
      {hint && <p className="enter-key-hint">{hint}</p>}
      <p className="muted">{t('enterKey.lead')}</p>
      {/* どのプロジェクトの・どちらで使うかを 1 行に（公開中のアプリは目立たせる）。名前と URL はテキストとして描く */}
      <p className={env === 'production' ? 'alert alert-info' : 'small'}>
        {project.data?.project.name && (
          <>
            <strong>{project.data.project.name}</strong>
            {' — '}
          </>
        )}
        {appUrl ? t(`enterKey.where.${env}`, { url: appUrl }) : t(`enterKey.where.${env}NoUrl`)}
      </p>
      {/* method="post": JS が止まって既定の送信が走っても、値を URL に乗せない */}
      <form method="post" onSubmit={submit} autoComplete="off">
        <label className="field">
          <span>{t('enterKey.label')}</span>
          <input
            ref={inputRef}
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('enterKey.placeholder')}
            autoComplete="off"
            autoFocus
            spellCheck={false}
            data-1p-ignore="true"
            data-lpignore="true"
            data-bwignore="true"
            data-form-type="other"
          />
          {touched && issue && <small className="field-error">{t(issue === 'empty' ? 'enterKey.empty' : 'enterKey.tooLong')}</small>}
        </label>
        <p className="small muted">{t('enterKey.safe')}</p>
        {err && (
          <p className="alert alert-error" role="alert">
            {err}
          </p>
        )}
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy}>
            {busy ? t('common.working') : t('enterKey.save')}
          </button>
        </div>
        {/* 名前は AI が決めた保存の名前（本人は何もしなくてよい）。小さく添えるだけ = 名前の中の API などの字は言葉ではない */}
        <p className="small muted">
          {t('enterKey.nameNote', { name })}
        </p>
      </form>
      <ConfirmDialog
        open={confirm}
        title={t('enterKey.confirmTitle')}
        confirmLabel={t('enterKey.confirmButton')}
        tone="danger"
        busy={busy}
        onClose={() => setConfirm(false)}
        onConfirm={() => void put()}
      >
        <p>{t('enterKey.confirmBody')}</p>
      </ConfirmDialog>
    </AuthShell>
  );
}
